/**
 * DMP Panel
 *
 * Main expandable panel for the Data Management Plan in the hub.
 * Integrates section list, WYSIWYG editor, status bar, and generation.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  doc,
  onSnapshot,
  updateDoc,
  getDocs,
  query,
  orderBy,
  where,
  limit,
  Timestamp,
  runTransaction,
  deleteField,
  serverTimestamp,
} from 'firebase/firestore';
import { decideDmpSectionSave } from '../../features/documents/dmpSectionSave';
import {
  clearReviewFields,
  dmpIssueBlockedReason,
  patchSectionEntry,
  reviewControlState,
  reviewedByLine,
  reviewRecord,
  statusAfterUnreview,
  textSaveFields,
} from '../../features/documents/dmpSectionReview';
import {
  applyDiscard,
  clearStaleSection,
  COPY_MY_TEXT_LABEL,
  plainTextOf,
  sectionSwitchGuard,
  shouldObserveSectionVersion,
  STALE_SECTION_BANNER,
  switchConfirmText,
  SWITCH_DISCARD_LABEL,
  SWITCH_KEEP_LABEL,
  type EditorEditState,
  type SectionSaveResult,
} from '../../features/documents/dmpEditorSync';
import { dmpDocumentSummary, nextArtifactStatus } from '../../features/documents/documentSummary';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../../constants/firebase';
import {
  artifactsCollection,
  dataManagementDoc,
  dataManagementSubcollection,
  projectDoc,
} from '../../constants/firestorePaths';
import { VersionedDoc, readDocVersion, docChangedMessage } from '../../utils/versionedDoc';
import { versionedDocFor } from '../../utils/versionedFirestoreDoc';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { saveAs } from 'file-saver';
import DMPSectionList from './DMPSectionList';
import DMPStatusBar from './DMPStatusBar';
import DMPEditor, { type DMPEditorControl } from './DMPEditor';
import { Loader2, Play, RefreshCw } from 'lucide-react';
import { EditorVersionBanner } from '../versioning/EditorVersionBanner';
import { VersionHistory, type LegacyRevisionEntry } from '../versioning/VersionHistory';
import { IssueVersionModal, type IssueVersionResult } from '../versioning/IssueVersionModal';
import { DOC_CODES } from '../../utils/versionedFilename';
import { nextMinorVersion } from '../../types/issuedVersion';

interface SectionMeta {
  id: string;
  number: number;
  title: string;
  group: string;
  sourceType: string;
  status: string;
  placeholderCount: number;
}

interface SectionContent {
  sectionId: string;
  number: number;
  title: string;
  group: string;
  sourceType: string;
  content: string;
  autoGeneratedContent: string;
  userEdited: boolean;
  placeholders: string[];
  status: string;
  lastEditedAt: any;
  lastEditedBy: string;
  reviewedBy?: string;
  reviewedByName?: string;
  reviewedByEmail?: string;
  reviewedAt?: any;
}

interface DMPData {
  version: number;
  status: string;
  title: string;
  studyInfo: any;
  sections: SectionMeta[];
  revisionHistory: any[];
  generatedAt: any;
  generatedBy: string;

  // Issued-version fields
  currentVersion?: string | null;
  draftTargetVersion?: string | null;
  editLocked?: boolean;
  currentVersionIssuedAt?: { toDate?: () => Date } | null;
}

interface ProjectInfo {
  shortName?: string | null;
  protocolNumber?: string | null;
  name?: string | null;
}

interface DMPPanelProps {
  projectId: string;
  /**
   * The Hub's in-flight state for this document, so ONE generation disables
   * EVERY control that can start it.
   *
   * This panel is rendered inside the Hub's dataManagementPlan card, which carries its
   * own Regenerate button, so two controls for the same action sit on screen
   * together. Each used to hold only its own state. Both do read the server's
   * `_generating` marker, but that is set only after a round trip, so at the
   * instant of a click neither could see the other's — and on 2026-08-17 one
   * click on the MCS card produced two requests 0.67ms apart, the second
   * refused by the per-study lock and shown to the user as a failure while the
   * first was succeeding.
   *
   * The Hub owns the authoritative state (its synchronous `generatingDocs`
   * ORed with the same marker), so passing it down closes that window
   * immediately rather than a round trip later.
   */
  generating?: boolean;
}

export default function DMPPanel({ projectId, generating: hubGenerating = false }: DMPPanelProps) {
  const [dmpData, setDmpData] = useState<DMPData | null>(null);
  // The DMP artifact's override record (who marked it Final over failing checks).
  const [dmpOverride, setDmpOverride] = useState<{ by?: string; at?: unknown; reason?: string; checks?: string[]; count?: number } | null>(null);
  useEffect(() => {
    if (!projectId) return;
    const q = query(artifactsCollection(projectId), where('type', '==', 'data_management_plan'), orderBy('createdAt', 'desc'), limit(1));
    return onSnapshot(q, (snap) => setDmpOverride(snap.empty ? null : (snap.docs[0].data().override ?? null)), () => setDmpOverride(null));
  }, [projectId]);
  const [sectionContents, setSectionContents] = useState<Map<string, SectionContent>>(new Map());
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState('');
  const [regeneratingSectionId, setRegeneratingSectionId] = useState<string | null>(null);

  // ADAPTED FROM THE OTHER SIX. This panel has no marker-derived guard — its
  // controls gate on the raw local `generating` — so there was no
  // `showGenerating` to fold the Hub's state into. One is introduced here
  // with the same NAME and role as the others, minus their Firestore-marker
  // clause: teaching this panel to read the marker would be a behaviour
  // change beyond closing the duplicate-control window, so it is not done.
  const showGenerating = generating || hubGenerating;
  const [exporting, setExporting] = useState(false);
  const [project, setProject] = useState<ProjectInfo | null>(null);
  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [startingRevision, setStartingRevision] = useState(false);
  const { showToast } = useToast();
  const { user } = useAuth();
  const [reviewBusy, setReviewBusy] = useState(false);

  // Optimistic concurrency, per DMP section: the version of each section's
  // text the editor has taken on. DMPEditor replaces its content when the
  // stored text arrives, EXCEPT for the section the user has unsaved typing
  // in: there it keeps the user's text, so a remote edit to that section is
  // not a take-on and its version is not observed (round 5d; the editor
  // reports what it holds through editStateRef).
  const sectionVersionsRef = useRef(new Map<string, VersionedDoc>());
  const editStateRef = useRef<EditorEditState | null>(null);
  const editorControlRef = useRef<DMPEditorControl | null>(null);
  const sectionVersion = useCallback((sectionId: string): VersionedDoc => {
    const key = projectId + '/' + sectionId;
    let v = sectionVersionsRef.current.get(key);
    if (!v) {
      v = versionedDocFor(doc(dataManagementDoc(projectId, 'dmp'), 'sections', sectionId));
      sectionVersionsRef.current.set(key, v);
    }
    return v;
  }, [projectId]);

  // Sections whose save was refused as changed elsewhere (round 5e). A
  // section stays stale until the page is reloaded or the user discards
  // their changes to it; only that section is cleared.
  const [staleSections, setStaleSections] = useState<Set<string>>(new Set());
  const markStale = useCallback((sectionId: string) => {
    setStaleSections((prev) => (prev.has(sectionId) ? prev : new Set(prev).add(sectionId)));
  }, []);
  const selectedIsStale = !!selectedSectionId && staleSections.has(selectedSectionId);
  // A section switch waiting for the user's choice.
  const [pendingSwitch, setPendingSwitch] = useState<string | null>(null);

  /** Switch section, unless that would silently drop refused text. */
  const requestSectionSwitch = useCallback((toSectionId: string) => {
    const guard = sectionSwitchGuard({
      fromSectionId: selectedSectionId,
      toSectionId,
      staleSections,
      editState: editStateRef.current,
    });
    if (guard === 'confirm') setPendingSwitch(toSectionId);
    else setSelectedSectionId(toSectionId);
  }, [selectedSectionId, staleSections]);

  /**
   * "Discard my changes and switch", in applyDiscard's order (round 5f):
   * cancel the editor's pending save FIRST, so the switch that follows has
   * nothing to flush and the discarded text is never saved; then clear that
   * section's stale state; then give it a fresh version tracker at the
   * version stored now; then switch.
   */
  const discardAndSwitch = useCallback(() => {
    const from = selectedSectionId;
    const to = pendingSwitch;
    setPendingSwitch(null);
    applyDiscard({
      cancelPending: () => editorControlRef.current?.cancelPending(),
      clearStale: () => {
        if (from) setStaleSections((prev) => clearStaleSection(prev, from));
      },
      freshTracker: () => {
        if (!from) return;
        sectionVersionsRef.current.delete(projectId + '/' + from);
        sectionVersion(from).observe(readDocVersion(sectionContents.get(from)));
      },
      switchSection: () => {
        if (to) setSelectedSectionId(to);
      },
    });
  }, [selectedSectionId, pendingSwitch, projectId, sectionVersion, sectionContents]);

  /** "Copy my text": the refused text, as plain text, to the clipboard. */
  const copyMyText = useCallback(async () => {
    // The editor's current text first, so the last unsaved typing is copied
    // too; then the refused text the tracker kept (round 5g).
    const kept = editorControlRef.current?.currentHtml() ?? editStateRef.current?.refusedHtml ?? editStateRef.current?.baselineHtml ?? '';
    try {
      await navigator.clipboard.writeText(plainTextOf(kept));
      showToast('Your text was copied.', 'success');
    } catch {
      showToast('Your text could not be copied. Select it in the editor and copy it by hand.', 'error');
    }
  }, [showToast]);

  // Subscribe to project doc for shortName/protocolNumber (used by the
  // Issue modal to preview the canonical filename).
  useEffect(() => {
    if (!projectId) return;
    const unsubscribe = onSnapshot(projectDoc(projectId), (snap) => {
      if (snap.exists()) {
        const data = snap.data() as ProjectInfo;
        setProject({
          shortName: data.shortName ?? null,
          protocolNumber: data.protocolNumber ?? null,
          name: data.name ?? null,
        });
      }
    });
    return () => unsubscribe();
  }, [projectId]);

  // Listen to DMP metadata document
  useEffect(() => {
    if (!projectId) return;

    const unsubscribe = onSnapshot(
      dataManagementDoc(projectId, 'dmp'),
      (snapshot) => {
        // A document with no sections list is not a generated DMP: the first
        // generation writes an in-flight marker (`_generating`) to it before
        // any section exists, and a failed first run leaves only that marker.
        const raw = snapshot.exists() ? (snapshot.data() as DMPData) : null;
        if (raw && !Array.isArray(raw.sections)) {
          setDmpData(null);
        } else if (raw) {
          const data: DMPData = raw;
          setDmpData(data);

          // Auto-select first section if none selected
          if (!selectedSectionId && data.sections?.length > 0) {
            setSelectedSectionId(data.sections[0].id);
          }
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error loading DMP:', err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load section contents when DMP exists
  useEffect(() => {
    if (!projectId || !dmpData) return;

    const sectionsRef = dataManagementSubcollection(projectId, 'dmp', 'sections');

    const unsubscribe = onSnapshot(
      query(sectionsRef, orderBy('number')),
      (snapshot) => {
        const contents = new Map<string, SectionContent>();
        snapshot.docs.forEach((d) => {
          const data = d.data() as SectionContent;
          // The page records a section's version only where the editor takes
          // its text on. A snapshot of the section the user has unsaved typing
          // in, carrying something other than what the editor last saved or
          // loaded, is a remote edit: not observed, so the pending save is
          // refused as changed elsewhere (round 5d).
          if (shouldObserveSectionVersion({ sectionId: d.id, incomingContent: String(data?.content ?? ''), editState: editStateRef.current })) sectionVersion(d.id).observe(readDocVersion(data));
          contents.set(data.sectionId, data);
        });
        setSectionContents(contents);
      },
      (err) => {
        console.error('Error loading DMP sections:', err);
      }
    );

    return () => unsubscribe();
  }, [projectId, dmpData]);

  // Generate DMP
  const handleGenerate = async () => {
    setGenerating(true);
    setGenerationProgress('Preparing generation...');

    // Simulate progress messages
    const progressMessages = [
      'Analyzing protocol data...',
      'Generating Study Context sections...',
      'Generating Data Processing sections...',
      'Generating Compliance sections...',
      'Finalizing document...',
    ];
    let step = 0;
    const progressTimer = setInterval(() => {
      step++;
      if (step < progressMessages.length) {
        setGenerationProgress(progressMessages[step]);
      }
    }, 15000);

    try {
      const generateDMP = httpsCallable(functions, CLOUD_FUNCTIONS.GENERATE_COMPREHENSIVE_DMP_V2, { timeout: CALLABLE_TIMEOUTS.GENERATOR_XLARGE });
      const result = await generateDMP({ projectId });
      const data = result.data as any;

      if (data.success) {
        setGenerationProgress(
          `Generated ${data.sectionCount} sections with ${data.placeholderCount} placeholders`
        );
      }
    } catch (err: any) {
      console.error('DMP generation error:', err);
      showToast(err.message || 'Failed to generate DMP', 'error');
    } finally {
      clearInterval(progressTimer);
      setGenerating(false);
      setGenerationProgress('');
    }
  };

  /**
   * Update one section's entry in the DMP's status list. Read and written in
   * one transaction, so another section's entry that changed since this page
   * loaded is not put back. The same transaction recomputes the plan's summary
   * and the artifact status with the server's rule (round 5): ONE answer to
   * "is it done?". Used by a text save and by marking a section reviewed.
   */
  const syncDmpSectionEntry = useCallback(
    async (sectionId: string, patch: { status: string; placeholderCount?: number }) => {
      if (!dmpData) return;
      const dmpRef = dataManagementDoc(projectId, 'dmp');
      const artifactSnap = await getDocs(
        query(artifactsCollection(projectId), where('type', '==', 'data_management_plan'), orderBy('createdAt', 'desc'), limit(1)),
      );
      const artifactRef = artifactSnap.empty ? null : artifactSnap.docs[0].ref;
      await runTransaction(db, async (tx) => {
        const stored = (await tx.get(dmpRef)).data() as DMPData | undefined;
        const artifact = artifactRef ? (await tx.get(artifactRef)).data() : undefined;
        if (!stored?.sections) return;
        const updatedSections = patchSectionEntry(stored.sections, sectionId, patch);
        const summary = dmpDocumentSummary(updatedSections);
        tx.update(dmpRef, {
          sections: updatedSections,
          ...summary,
          lastModifiedAt: Timestamp.now(),
        });
        const nextStatus = artifact ? nextArtifactStatus(artifact.status, summary.generationStatus) : null;
        if (artifactRef && nextStatus) tx.update(artifactRef, { status: nextStatus });
      });
    },
    [projectId, dmpData],
  );

  // Save section content
  const handleSaveSection = useCallback(
    async (html: string, info: { userChanged: boolean; sectionId?: string } = { userChanged: false }): Promise<SectionSaveResult> => {
      // The section the text belongs to, which the editor names: a save
      // flushed on a section switch carries the section being LEFT, not the
      // one now selected (docgen reasons, round 5c).
      const sectionId = info.sectionId ?? selectedSectionId;
      if (!projectId || !sectionId) return 'skipped';
      // While the document is locked (issued, no revision in progress),
      // section autosaves are silently dropped. The banner prompts the
      // user to "Start revision" before they can edit.
      if (dmpData?.editLocked) return 'skipped';

      const sectionRef = doc(
        dataManagementSubcollection(projectId, 'dmp', 'sections'),
        sectionId,
      );

      const currentContent = sectionContents.get(sectionId);
      // Nothing is saved unless the user changed the text, and a failed
      // section's notice is never saved as user content (docgen reasons,
      // round 5; features/documents/dmpSectionSave.ts).
      const decision = decideDmpSectionSave({
        html,
        userChanged: info.userChanged,
        stored: {
          content: currentContent?.content,
          autoGeneratedContent: currentContent?.autoGeneratedContent,
          status: currentContent?.status,
        },
      });
      if (!decision.save) return 'skipped';
      const isUserEdited = decision.userEdited;
      const sectionStatus = decision.status;

      // Count remaining placeholders
      const placeholderMatches = html.match(/\[PLACEHOLDER:[^\]]+\]/g) || [];
      const placeholders = placeholderMatches.map((m) =>
        m.replace('[PLACEHOLDER: ', '').replace(']', '')
      );

      // Runs after the section write, WITHOUT delaying the editor's
      // confirmation (round 5h): a confirmation that waits on this widens
      // the window in which a remote edit arrives before the answer.
      const syncDmpStatus = () =>
        syncDmpSectionEntry(sectionId, { status: sectionStatus, placeholderCount: placeholders.length });

      try {
        // The section's whole text, from the editor: claims the version the
        // editor took on plus one, and is refused if the stored text has
        // moved since (another tab, a regeneration).
        const outcome = await sectionVersion(sectionId).write((next) =>
          updateDoc(
            sectionRef,
            // A text change ends any review of the old text (dmpSectionReview.ts).
            textSaveFields({
              html,
              userEdited: isUserEdited,
              placeholders,
              status: sectionStatus,
              docVersion: next,
              now: Timestamp.now(),
              deleteMarker: deleteField,
            }),
          ),
        );
        if (outcome === 'stale' || outcome === 'blocked') {
          // Refused as changed elsewhere. The toast is said once; the section
          // then carries a persistent banner, and switching away from it asks
          // first, for as long as it stays stale (round 5e).
          markStale(sectionId);
          if (outcome === 'stale') showToast(docChangedMessage('This DMP section'), 'error');
          return 'refused';
        }
        // The version check accepted the write: the text is stored (round 5g).
        // Answer now; the status list and artifact follow (round 5h).
        void syncDmpStatus().catch((err) => console.error('Error updating the DMP status after a section save:', err));
        return 'saved';
      } catch (err) {
        console.error('Error saving section:', err);
        return 'skipped';
      }
    },
    [projectId, selectedSectionId, sectionContents, dmpData, sectionVersion, showToast, syncDmpSectionEntry, markStale]
  );

  // Mark a section reviewed, or not reviewed. Same section document, same
  // version check and same status-list transaction as a text save.
  const handleSetReviewed = useCallback(
    async (sectionId: string, reviewed: boolean) => {
      if (!projectId || !user || reviewBusy) return;
      // A locked document (issued, no revision in progress) cannot change review state.
      if (dmpData?.editLocked) return;
      const current = sectionContents.get(sectionId);
      const control = reviewControlState({
        status: current?.status,
        content: current?.content,
        editLocked: !!dmpData?.editLocked,
        stale: staleSections.has(sectionId),
      });
      if (!control.enabled || control.mode !== (reviewed ? 'mark' : 'unmark')) return;
      // Typing not yet saved would be reviewed without being stored.
      const es = editStateRef.current;
      if (es && es.sectionId === sectionId && es.pendingSave) {
        showToast('Your latest changes are still saving. Try again in a moment.', 'info');
        return;
      }
      const sectionRef = doc(dataManagementSubcollection(projectId, 'dmp', 'sections'), sectionId);
      const nextStatus = reviewed ? 'reviewed' : statusAfterUnreview({ userEdited: current?.userEdited });
      setReviewBusy(true);
      try {
        const outcome = await sectionVersion(sectionId).write((next) =>
          updateDoc(
            sectionRef,
            reviewed
              ? { ...reviewRecord(user, serverTimestamp()), docVersion: next }
              : { status: nextStatus, ...clearReviewFields(deleteField), docVersion: next },
          ),
        );
        if (outcome === 'stale' || outcome === 'blocked') {
          markStale(sectionId);
          if (outcome === 'stale') showToast(docChangedMessage('This DMP section'), 'error');
          return;
        }
        try {
          await syncDmpSectionEntry(sectionId, { status: nextStatus });
        } catch (err) {
          console.error('Error updating the DMP status after a review change:', err);
          showToast('The review was saved, but the document status could not be updated. Try again.', 'error');
        }
      } catch (err) {
        console.error('Error saving review:', err);
        showToast('The review could not be saved. Try again.', 'error');
      } finally {
        setReviewBusy(false);
      }
    },
    [projectId, user, reviewBusy, dmpData, sectionContents, staleSections, sectionVersion, showToast, markStale, syncDmpSectionEntry],
  );

  // Regenerate a single section
  const handleRegenerateSection = useCallback(
    async (sectionId: string) => {
      if (!projectId || generating) return;

      setRegeneratingSectionId(sectionId);

      try {
        const regenFn = httpsCallable(functions, CLOUD_FUNCTIONS.REGENERATE_DMP_SECTION, { timeout: CALLABLE_TIMEOUTS.SHORT });
        await regenFn({ projectId, sectionId });
        // Content updates arrive via onSnapshot automatically
      } catch (err: any) {
        console.error('DMP section regeneration error:', err);
        showToast(err.message || 'Failed to regenerate section', 'error');
      } finally {
        setRegeneratingSectionId(null);
      }
    },
    [projectId, generating]
  );

  // Export DOCX
  const handleExport = async () => {
    setExporting(true);
    try {
      const exportFn = httpsCallable(functions, CLOUD_FUNCTIONS.EXPORT_DMP_DOCX);
      const result = await exportFn({ projectId });
      const data = result.data as { docx: string; filename: string };

      // Convert base64 to blob and download
      const byteCharacters = atob(data.docx);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      saveAs(blob, data.filename);
    } catch (err: any) {
      console.error('DMP export error:', err);
      showToast(err.message || 'Failed to export DMP', 'error');
    } finally {
      setExporting(false);
    }
  };

  // ── Version control handlers ──────────────────────────────────────

  const handleStartRevision = useCallback(async () => {
    if (!projectId || !dmpData) return;
    setStartingRevision(true);
    try {
      const target = nextMinorVersion(dmpData.currentVersion);
      await updateDoc(dataManagementDoc(projectId, 'dmp'), {
        draftTargetVersion: target,
        editLocked: false,
        lastModifiedAt: Timestamp.now(),
      });
      showToast(`Started draft of v${target}. Sections are now editable.`, 'success');
    } catch (err: any) {
      console.error('Start revision error:', err);
      showToast(err.message || 'Failed to start revision', 'error');
    } finally {
      setStartingRevision(false);
    }
  }, [projectId, dmpData, showToast]);

  const handleDiscardDraft = useCallback(async () => {
    if (!projectId || !dmpData) return;
    if (
      !confirm(
        'Discard this revision draft? Section content edits remain in place; only the draft state is cleared.',
      )
    ) {
      return;
    }
    try {
      await updateDoc(dataManagementDoc(projectId, 'dmp'), {
        draftTargetVersion: null,
        editLocked: true,
        lastModifiedAt: Timestamp.now(),
      });
      showToast('Draft discarded; document is locked again.', 'info');
    } catch (err: any) {
      console.error('Discard draft error:', err);
      showToast(err.message || 'Failed to discard draft', 'error');
    }
  }, [projectId, dmpData, showToast]);

  const handleIssueSuccess = useCallback(
    (result: IssueVersionResult) => {
      showToast(`Issued v${result.versionString} of the DMP.`, 'success');
    },
    [showToast],
  );

  // Issue is blocked while sections still need review. Per the plan,
  // multi-section docs gate the Issue button on per-section approval.
  const issueBlockedReason = useMemo(() => {
    return dmpIssueBlockedReason(dmpData?.sections);
  }, [dmpData?.sections]);

  // Map the legacy revisionHistory array (existing field on DMPDocument)
  // into the shape VersionHistory expects for the "Legacy revision notes"
  // divider section.
  const legacyEntries: LegacyRevisionEntry[] = useMemo(() => {
    const history = dmpData?.revisionHistory || [];
    return history.map((entry: any) => ({
      version: String(entry.version ?? ''),
      date:
        entry.date && typeof entry.date.toDate === 'function'
          ? entry.date.toDate()
          : null,
      author: String(entry.author ?? ''),
      description: String(entry.description ?? ''),
    }));
  }, [dmpData?.revisionHistory]);

  const editLocked = !!dmpData?.editLocked;

  // Compute completion stats
  // A section is "complete" once it has content (generated/edited/reviewed)
  const totalSections = dmpData?.sections?.length || 0;
  const completeSections =
    dmpData?.sections?.filter(
      (s) => s.status === 'generated' || s.status === 'edited' || s.status === 'reviewed'
    ).length || 0;
  const totalPlaceholders =
    dmpData?.sections?.reduce((sum, s) => sum + (s.placeholderCount || 0), 0) || 0;

  const selectedContent = selectedSectionId ? sectionContents.get(selectedSectionId) : null;

  // Sections the generator could not write. They carry a notice, not content,
  // and are named here so they are not mistaken for written sections.
  const failedSections = (dmpData?.sections || []).filter((s) => s.status === 'generation_failed');

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  // Not yet generated - show generation UI
  if (!dmpData) {
    return (
      <div className="space-y-4">
        <div className="text-center py-8">
          <p className="text-gray-500 mb-2">
            Generate a comprehensive Data Management Plan with 18 sections covering study context,
            data processing, and regulatory compliance.
          </p>
          <p className="text-xs text-gray-400 mb-6">
            Content is generated from your protocol analysis, study design, CRFs, and data
            dictionary. Sections requiring your input will include highlighted placeholders.
          </p>


          {showGenerating ? (
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-2 text-sm text-blue-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                {generationProgress || 'Generating...'}
              </div>
              <div className="max-w-xs mx-auto">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full animate-pulse w-2/3" />
                </div>
              </div>
              <p className="text-xs text-gray-400">
                This may take 2-3 minutes as each section is generated individually.
              </p>
            </div>
          ) : (
            <button
              onClick={handleGenerate}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium"
            >
              <Play className="h-4 w-4" />
              Generate Data Management Plan
            </button>
          )}
        </div>
      </div>
    );
  }

  // DMP exists - show editor UI
  return (
    <div className="space-y-3">
      {/* Issued-version banner */}
      <EditorVersionBanner
        currentVersion={dmpData.currentVersion}
        currentVersionIssuedAt={
          dmpData.currentVersionIssuedAt && typeof dmpData.currentVersionIssuedAt.toDate === 'function'
            ? dmpData.currentVersionIssuedAt.toDate()
            : null
        }
        draftTargetVersion={dmpData.draftTargetVersion}
        editLocked={editLocked}
        onStartRevision={handleStartRevision}
        onApproveAndIssue={() => setIssueModalOpen(true)}
        onDiscardDraft={dmpData.draftTargetVersion ? handleDiscardDraft : undefined}
        busy={startingRevision || generating}
        issueBlockedReason={issueBlockedReason}
      />

      {/* Version history (collapsed by default) */}
      <VersionHistory
        projectId={projectId}
        sourceDocFamily="dataManagement"
        sourceDocId="dmp"
        legacyEntries={legacyEntries}
      />

      {/* Status bar */}
      <DMPStatusBar
        version={dmpData.version}
        status={dmpData.status}
        override={dmpOverride}
        totalSections={totalSections}
        completeSections={completeSections}
        totalPlaceholders={totalPlaceholders}
        onExport={handleExport}
        exporting={exporting}
      />

      {failedSections.length > 0 && (
        <div className="border border-red-200 bg-red-50 text-red-800 text-sm rounded-lg px-3 py-2">
          {failedSections.length === 1 ? 'One section' : `${failedSections.length} sections`} could not be written
          automatically: {failedSections.map((s) => `${s.number}. ${s.title}`).join('; ')}. Regenerate
          {failedSections.length === 1 ? ' it' : ' them'}, or write {failedSections.length === 1 ? 'it' : 'them'}, before review.
        </div>
      )}

      {/* Main content: section list + editor */}
      <div className="flex gap-3">
        {/* Section navigator */}
        <div className="w-72 flex-shrink-0 border border-gray-200 rounded-lg bg-white p-2 max-h-[600px] overflow-y-auto">
          <DMPSectionList
            sections={dmpData.sections || []}
            selectedSectionId={selectedSectionId}
            onSelectSection={requestSectionSwitch}
            onRegenerateSection={handleRegenerateSection}
            regeneratingSectionId={regeneratingSectionId}
          />
        </div>

        {/* Editor */}
        <div className="flex-1 min-w-0">
          {selectedContent ? (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-800">
                  {selectedContent.number}. {selectedContent.title}
                </h3>
                {selectedContent.sourceType === 'partial' && (
                  <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                    Requires user input
                  </span>
                )}
              </div>
              {(() => {
                const rc = reviewControlState({
                  status: selectedContent.status,
                  content: selectedContent.content,
                  editLocked,
                  stale: selectedIsStale,
                });
                const reviewedNow = selectedContent.status === 'reviewed';
                return (
                  <div className="mb-2 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => void handleSetReviewed(selectedContent.sectionId, !reviewedNow)}
                      disabled={!rc.enabled || reviewBusy}
                      title={rc.reason ?? undefined}
                      className="rounded border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {rc.label}
                    </button>
                    {reviewedNow && <span className="text-xs text-green-700">{reviewedByLine(selectedContent)}</span>}
                    {rc.note && <span className="text-xs text-amber-700">{rc.note}</span>}
                    {!rc.enabled && rc.reason && !editLocked && <span className="text-xs text-gray-500">{rc.reason}</span>}
                  </div>
                );
              })()}
              {/* A save refused as changed elsewhere: said for as long as it lasts (round 5e). */}
              {selectedIsStale && (
                <div role="alert" className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                  <p>{STALE_SECTION_BANNER}</p>
                  <button
                    type="button"
                    onClick={copyMyText}
                    className="mt-2 rounded border border-red-300 bg-white px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                  >
                    {COPY_MY_TEXT_LABEL}
                  </button>
                </div>
              )}
              {/* Switching away from a stale or kept section that holds the user's text asks first (round 5i). */}
              {pendingSwitch && (
                <div role="dialog" aria-modal="true" className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  <p>{switchConfirmText({ fromSectionId: selectedSectionId, staleSections })}</p>
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      onClick={() => setPendingSwitch(null)}
                      className="rounded border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      {SWITCH_KEEP_LABEL}
                    </button>
                    <button
                      type="button"
                      onClick={discardAndSwitch}
                      className="rounded border border-red-300 bg-white px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                    >
                      {SWITCH_DISCARD_LABEL}
                    </button>
                  </div>
                </div>
              )}
              <DMPEditor
                sectionId={selectedContent.sectionId}
                content={selectedContent.content}
                autoGeneratedContent={selectedContent.autoGeneratedContent}
                onSave={handleSaveSection}
                // Read-only while the switch question is shown, so it is modal (round 5f).
                readOnly={editLocked || !!pendingSwitch}
                editState={editStateRef}
                stale={selectedIsStale}
                control={editorControlRef}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-400 text-sm border border-gray-200 rounded-lg bg-white">
              Select a section from the list to view and edit
            </div>
          )}
        </div>
      </div>

      {/* Regenerate option */}
      <div className="flex justify-end">
        <button
          onClick={() => {
            const editedSections = (dmpData?.sections || []).filter(
              (s: any) => s.status === 'edited'
            );
            const msg =
              editedSections.length > 0
                ? `This will regenerate all sections. ${editedSections.length} section(s) with your edits will be overwritten:\n\n${editedSections.map((s: any) => `- ${s.title}`).join('\n')}\n\nContinue?`
                : 'Regenerate all DMP sections with the latest study data?';
            if (confirm(msg)) {
              handleGenerate();
            }
          }}
          disabled={showGenerating}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${showGenerating ? 'animate-spin' : ''}`} />
          Regenerate All Sections
        </button>
      </div>

      <IssueVersionModal
        isOpen={issueModalOpen}
        onClose={() => setIssueModalOpen(false)}
        projectId={projectId}
        sourceDocFamily="dataManagement"
        sourceDocId="dmp"
        docCode={DOC_CODES.dmp}
        docName={dmpData.title || 'Data Management Plan'}
        shortName={project?.shortName}
        protocolNumber={project?.protocolNumber}
        currentVersion={dmpData.currentVersion}
        onSuccess={handleIssueSuccess}
      />
    </div>
  );
}
